import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useRegisterCounts } from "../../api/quotations";
import { useMe } from "../../auth/session";
import "../../styles/commercial.css";
import ReaderDrawer from "../quotations/ReaderDrawer";
import AllJobs from "./AllJobs";
import CommercialWorkspace from "./CommercialWorkspace";
import DocumentsPanel from "./DocumentsPanel";
import JobsRail, { count } from "./JobsRail";
import StartDraftBoq from "./StartDraftBoq";
import { stepFromParam, type StepKey } from "./pipeline";

/** The commercial chain on one screen, as a split view (Fix #9).
 *
 * The rail on the left is every job, arranged by what it waits on; the right
 * is the job you picked: its header, the one next step, the six documents as
 * a pipeline, and the chosen document under it. The old screen stacked the
 * intake, a register up to 60vh tall and a ledger of every document above the
 * job, so choosing a row changed nothing on screen; here the job is always
 * beside the list.
 *
 * The selection and the open step are both the address -- `/commercial/:id`
 * and `?tab=` -- so a row is a link, the back button means something, and a
 * document can be sent to somebody. Reading a supplier's PDF is a drawer over
 * the screen (Fix #13), and the whole register, a page at a time, is an
 * overlay over it. */
export default function CommercialPage() {
  const { id } = useParams();
  const selected = id === undefined ? null : Number(id);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const me = useMe();
  const office = me.data?.role === "office";
  const supplierView = me.data?.role === "supplier";
  const [reading, setReading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  // Choosing a job puts the draft form away.
  useEffect(() => setDrafting(false), [selected]);
  const [allJobs, setAllJobs] = useState(false);
  const [minimised, setMinimised] = useState(false);
  const [wide, setWide] = useState(false);

  const step = stepFromParam(params.get("tab"));
  const read = params.get("read") === "1";

  function openStep(next: StepKey, options: { read?: boolean } = {}) {
    const search = new URLSearchParams(params);
    search.set("tab", next);
    if (options.read) {
      search.set("read", "1");
    } else {
      search.delete("read");
    }
    setParams(search, { replace: true });
  }

  const layout = ["split", "cm-split", wide ? "cm-wide" : "", minimised ? "cm-min" : ""].filter(Boolean).join(" ");

  return (
    <div className="page-bleed commercial-page">
      <div className={layout}>
        {minimised ? (
          <MiniRail
            canRead={office}
            onRestore={() => setMinimised(false)}
            onRead={() => setReading(true)}
            onOpenAll={() => setAllJobs(true)}
          />
        ) : (
          <JobsRail
            selectedId={selected}
            supplierView={supplierView}
            canRead={office}
            onRead={() => setReading(true)}
            onDraft={() => setDrafting(true)}
            onOpenAll={() => setAllJobs(true)}
            onMinimise={() => setMinimised(true)}
            wide={wide}
            onToggleWide={() => setWide((now) => !now)}
          />
        )}
        <div className="split-main cm-main">
          {drafting ? (
            <StartDraftBoq
              onCancel={() => setDrafting(false)}
              onStarted={(boqId) => {
                setDrafting(false);
                navigate(`/boq/${boqId}`);
              }}
            />
          ) : selected === null ? (
            <NothingChosen office={office} supplierView={supplierView} onRead={() => setReading(true)} />
          ) : (
            <CommercialWorkspace
              key={selected}
              id={selected}
              step={step}
              read={read}
              onStep={openStep}
              office={office}
              supplierView={supplierView}
            />
          )}
        </div>
      </div>

      {reading ? (
        <ReaderDrawer
          onClose={() => setReading(false)}
          onSaved={(quotationId) => {
            setReading(false);
            navigate(`/commercial/${quotationId}?tab=quote`);
          }}
        />
      ) : null}

      {allJobs ? (
        <AllJobs
          selectedId={selected}
          supplierView={supplierView}
          onClose={() => setAllJobs(false)}
          onPick={(quotationId) => {
            setAllJobs(false);
            navigate(`/commercial/${quotationId}`);
          }}
        />
      ) : null}
    </div>
  );
}

/** The rail folded to a strip: enough to bring it back, reach every job, or
 * read a quotation in, and nothing that takes width from the job. */
function MiniRail({
  canRead,
  onRestore,
  onRead,
  onOpenAll,
}: {
  canRead: boolean;
  onRestore: () => void;
  onRead: () => void;
  onOpenAll: () => void;
}) {
  const everything = useRegisterCounts("");
  return (
    <aside className="split-rail cm-mini" aria-label="Jobs">
      <button type="button" className="cm-icon" onClick={onRestore} title="Show the list" aria-label="Show the list">
        »
      </button>
      <button type="button" className="cm-mini-jobs" onClick={onOpenAll} title="All jobs">
        <span>Jobs</span>
        <span className="cm-rail-count">{count(everything.data?.counts.all)}</span>
      </button>
      {canRead ? (
        <button type="button" className="btn btn-primary cm-mini-read" onClick={onRead} title="Read a quotation">
          + Read
        </button>
      ) : null}
    </aside>
  );
}

/** What the right side says before a job is chosen, in the words of whoever
 * is looking (Fix #21): a supplier cannot read a quotation in, so is not told
 * to. The office also gets every document raised, across all jobs. */
function NothingChosen({
  office,
  supplierView,
  onRead,
}: {
  office: boolean;
  supplierView: boolean;
  onRead: () => void;
}) {
  const everything = useRegisterCounts("");
  const none = everything.data?.counts.all === 0;
  return (
    <div className="cm-job cm-nothing">
      <div className="empty-state">
        {supplierView ? (
          <p>
            {none
              ? "None of your quotations has been captured yet. They appear here once the office has them."
              : "Choose one of your quotations on the left to see where it stands."}
          </p>
        ) : (
          <>
            <p>
              {none
                ? "No jobs yet. A job starts when a supplier's quotation is read in."
                : "Choose a job on the left, or read a supplier's quotation in to start one."}
            </p>
            {office ? (
              <button type="button" className="btn btn-primary" onClick={onRead}>
                + Read a quotation
              </button>
            ) : null}
          </>
        )}
      </div>
      {office ? <DocumentsPanel /> : null}
    </div>
  );
}
